import { useParams, useNavigate } from 'react-router-dom'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Chip from '@mui/material/Chip'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import { useTranslation } from 'react-i18next'
import { useShop, useTrackOrder } from '../shared/api/storefront'
import { formatMoney } from '../shared/money'
import { formatDateTime } from '../shared/date'

/** Chip colour per order status; the (translated) label comes from `trackStatus.*` (§ i18n). */
const STATUS_COLOR: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  NEW: 'info',
  PREPARING: 'warning',
  READY: 'success',
  DELIVERED: 'success',
  CANCELLED: 'error',
}

export default function TrackPage() {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()
  const { data: shop } = useShop()
  const currency = shop?.currency ?? 'INR'
  const { data: order, isLoading, isError } = useTrackOrder(token)
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }
  if (isError || !order) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error" action={<Button onClick={() => navigate('/')}>{t('common.backToShop')}</Button>}>
          {t('track.notFound')}
        </Alert>
      </Container>
    )
  }

  const statusColor = STATUS_COLOR[order.status] ?? 'default'
  const statusLabel = t(`trackStatus.${order.status}`, { defaultValue: order.status })
  // Priceless order (D-069): null total → show items + quantities, no money.
  const showMoney = order.total != null

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          {t('common.orderNumber', { number: order.orderNumber })}
        </Typography>
        <Chip label={statusLabel} color={statusColor} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {order.paymentMethod === 'COD' ? t('checkout.cashOnDelivery') : order.paymentMethod} ·{' '}
        {order.paymentStatus === 'PAID' ? t('track.paid') : t('track.paymentPending')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
        {formatDateTime(order.createdAt)}
      </Typography>

      <Stack spacing={1} sx={{ mb: 3 }}>
        {order.items.map((item) => (
          <Stack key={item.id} direction="row" justifyContent="space-between">
            <Typography variant="body2" sx={{ mr: 1 }}>
              {item.productName} × {item.quantity}
              {item.options && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {item.options}
                </Typography>
              )}
              {/* The shop's physical leg tag for this line's bird, once staff tie one on (D-102).
                  Shown per line so a multi-bird order carries each tag; it is what the customer
                  shows across the counter to claim the right bird. Absent until staff assign one. */}
              {item.tagNumber && (
                <Typography component="span" sx={{ display: 'block', fontWeight: 800, color: 'primary.main' }}>
                  {t('track.tagNumber')}: {item.tagNumber}
                </Typography>
              )}
            </Typography>
            {showMoney && item.lineTotal != null && (
              <Typography variant="body2">{formatMoney(item.lineTotal, currency)}</Typography>
            )}
          </Stack>
        ))}
        {showMoney && (
          <>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight={700}>
                {t('common.total')}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700}>
                {formatMoney(order.total as number, currency)}
              </Typography>
            </Stack>
          </>
        )}
      </Stack>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2">
          {order.fulfillmentType === 'PICKUP' ? t('track.pickupAtShop') : t('track.deliveringTo')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {order.customerName} · {order.customerPhone}
        </Typography>
        {order.fulfillmentType === 'DELIVERY' && order.deliveryAddress && (
          <Typography variant="body2" color="text.secondary">
            {order.deliveryAddress}
          </Typography>
        )}
      </Box>

      {order.notes && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2">{t('track.notes')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {order.notes}
          </Typography>
        </Box>
      )}

      <Button variant="outlined" onClick={() => navigate('/')}>
        {t('common.backToShop')}
      </Button>
    </Container>
  )
}
